import { FaPlus, FaClock, FaFileSignature, FaMapMarkerAlt, FaBriefcase, FaTag } from 'react-icons/fa'
import { useMyOfferings, useUpdateOffering } from '@/hooks/useOfferings'
import routes from '@/routes/routes'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/common/Button/Button'
import './ProviderServices.css'
import { useMemo, useState } from 'react'

type PricingType = 'hourly' | 'quote'

type District = {
    id: number
    name: string
}

type MyOffering = {
    id: number
    service: number
    service_name?: string
    service_category?: string
    pricing_type: PricingType
    hourly_rate?: number | null
    minimum_hours?: number | null
    base_quote_amount?: number | null
    quote_notes?: string | null
    requires_quote_visit?: boolean
    description?: string | null
    districts_details?: District[]
    is_active: boolean
}

const currencyFormatter = new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
})

const formatCurrency = (value?: number | null) => {
    if (typeof value !== 'number') return null
    return currencyFormatter.format(value)
}

const formatCoverage = (districts?: District[]) => {
    if (!districts || districts.length === 0) {
        return 'Usa la cobertura general del perfil'
    }

    if (districts.length <= 3) {
        return districts.map((d) => d.name).join(', ')
    }

    const [first, second, third] = districts
    return `${first.name}, ${second.name}, ${third.name} +${districts.length - 3}`
}

const ProviderServices = () => {
    const { data, isLoading, isError } = useMyOfferings()
    const navigate = useNavigate()
    const [updatingId, setUpdatingId] = useState<number | null>(null)
    const updateOffering = useUpdateOffering()

    const list: MyOffering[] = useMemo(() => Array.isArray(data) ? data : [], [data])

    const handleToggleStatus = async (offering: MyOffering) => {
        if (updatingId) return
        try {
            setUpdatingId(offering.id)
            await updateOffering.mutateAsync({
                id: offering.id,
                data: { is_active: !offering.is_active },
            })
        } catch (error) {
            console.error('Error al actualizar el estado del servicio', error)
            alert('No pudimos actualizar el estado de este servicio. Intenta nuevamente.')
        } finally {
            setUpdatingId(null)
        }
    }

    const handleConfigurePricing = (offering: MyOffering) => {
        const url = `${routes.providerSetup}?step=pricing&serviceId=${offering.service}`
        navigate(url)
    }

    return (
        <div className="provider-services-page">
            {/* Header */}
            <div className="provider-services-header">
                <div className="provider-services-header-content">
                    <h1 className="provider-services-title">Mis Servicios</h1>
                    <p className="provider-services-subtitle">{list.length} servicio{list.length !== 1 ? 's' : ''} publicado{list.length !== 1 ? 's' : ''}</p>
                </div>
                <Button
                    variant="primary"
                    size="lg"
                    onClick={() => navigate(`${routes.providerSetup}?step=services`)}
                >
                    <FaPlus />
                    Configurar servicios
                </Button>
            </div>

            <div className="provider-services-container">
                {/* Services List */}
                <div className="provider-services-list">
                    {isLoading ? (
                        <div className="provider-services-loading">
                            <p>Cargando servicios...</p>
                        </div>
                    ) : isError ? (
                        <div className="provider-services-error">
                            <p>Error al cargar servicios</p>
                        </div>
                    ) : list.length === 0 ? (
                        <div className="provider-services-empty">
                            <FaBriefcase className="provider-services-empty-icon" />
                            <h3 className="provider-services-empty-title">No tienes servicios</h3>
                            <p className="provider-services-empty-text">Agrega servicios para empezar a recibir solicitudes</p>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={() => navigate(`${routes.providerSetup}?step=services`)}
                            >
                                <FaPlus />
                                Agregar Primer Servicio
                            </Button>
                        </div>
                    ) : (
                        list.map((offering) => {
                            const hourlyRate = formatCurrency(offering.hourly_rate)
                            const baseQuote = formatCurrency(offering.base_quote_amount)
                            const coverage = formatCoverage(offering.districts_details)

                            return (
                                <div key={offering.id} className="provider-services-card">
                                    <div className="provider-services-card-header">
                                        <div className="provider-services-card-title-block">
                                            <h3 className="provider-services-card-title">
                                                {offering.service_name || 'Servicio sin nombre'}
                                            </h3>
                                            {offering.service_category && (
                                                <span className="provider-services-category">{offering.service_category}</span>
                                            )}
                                        </div>
                                        <span className={`provider-services-badge ${
                                            offering.is_active ? 'provider-services-badge--active' : 'provider-services-badge--inactive'
                                        }`}>
                                            {offering.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>

                                    <div className="provider-services-card-details">
                                        <div className="provider-services-detail">
                                            <FaTag className="provider-services-detail-icon" />
                                            <span>{offering.pricing_type === 'hourly' ? 'Cobro por hora' : 'Cotización'}</span>
                                        </div>
                                        {offering.pricing_type === 'hourly' ? (
                                            <div className="provider-services-detail">
                                                <FaClock className="provider-services-detail-icon" />
                                                <span>
                                                    {hourlyRate ? `${hourlyRate} por hora` : 'Tarifa pendiente'}
                                                    {offering.minimum_hours ? ` · mínimo ${offering.minimum_hours} h` : ''}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="provider-services-detail">
                                                <FaFileSignature className="provider-services-detail-icon" />
                                                <span>
                                                    {baseQuote ? `Monto de referencia ${baseQuote}` : 'Sin monto de referencia'}
                                                    {offering.requires_quote_visit ? ' · requiere visita' : ''}
                                                </span>
                                            </div>
                                        )}
                                        <div className="provider-services-detail provider-services-detail--full">
                                            <FaMapMarkerAlt className="provider-services-detail-icon" />
                                            <span>{`Cobertura: ${coverage}`}</span>
                                        </div>
                                    </div>

                                    {offering.description && (
                                        <div className="provider-services-note">
                                            <span>Descripción para clientes</span>
                                            <p>{offering.description}</p>
                                        </div>
                                    )}

                                    {offering.pricing_type === 'quote' && offering.quote_notes && (
                                        <div className="provider-services-note">
                                            <span>Notas para cotizaciones</span>
                                            <p>{offering.quote_notes}</p>
                                        </div>
                                    )}

                                    <div className="provider-services-card-actions">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleConfigurePricing(offering)}
                                        >
                                            Ajustar precios
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleToggleStatus(offering)}
                                            disabled={updatingId === offering.id}
                                        >
                                            {offering.is_active ? 'Pausar servicio' : 'Activar servicio'}
                                        </Button>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}

export default ProviderServices


